import React, { useState, useEffect, useRef } from 'react';
import {
  Users, UserPlus, Edit2, Trash2, X, Check, AlertCircle,
  Eye, EyeOff, ToggleLeft, ToggleRight, BarChart3, Database,
  Shield, Activity, RefreshCw, Search, CheckCircle2, UserCheck,
  TrendingUp, Calendar, GraduationCap, Plus, Save, Hash, School,
  Megaphone, Globe, Clock, CheckCircle, Upload, Download, FileText,
  ChevronDown, AlertTriangle, Sparkles, RotateCcw, Zap, Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const USER_TYPES = ['admin', 'digitizer', 'verifier', 'viewer'];
const OFFICE_TYPES = ['NEB Head Office', 'NEB Province Office', 'NEB Exam Center'];

const USER_TYPE_COLORS = {
  admin:     { badge: 'bg-purple-100 text-purple-700 border border-purple-200', dot: 'bg-purple-500' },
  digitizer: { badge: 'bg-blue-100 text-blue-700 border border-blue-200',     dot: 'bg-blue-500' },
  verifier:  { badge: 'bg-green-100 text-green-700 border border-green-200',   dot: 'bg-green-500' },
  viewer:    { badge: 'bg-gray-100 text-gray-600 border border-gray-200',      dot: 'bg-gray-400' },
};

function UserModal({ user, onSave, onClose }) {
  const { authFetch } = useAuth();
  const isEdit = !!user?.id;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    user_type: user?.user_type || 'digitizer',
    office_type: user?.office_type || 'NEB Exam Center',
    password: '',
    is_active: user?.is_active !== false,
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isEdit && !form.password) { setError('Password is required for new users'); return; }
    setLoading(true);
    try {
      const body = { ...form };
      if (isEdit && !body.password) delete body.password;
      const res = await authFetch(isEdit ? `/api/users/${user.id}` : '/api/users', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save user');
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">{isEdit ? 'Edit User' : 'Add New User'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="Full name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="email@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="98XXXXXXXX" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Type *</label>
              <select value={form.user_type} onChange={e => setForm(f => ({ ...f, user_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                {USER_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Type *</label>
              <select value={form.office_type} onChange={e => setForm(f => ({ ...f, office_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                {OFFICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}
              </label>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                  placeholder={isEdit ? 'Leave blank to keep current' : 'Enter password'} />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {isEdit && (
              <div className="sm:col-span-2 flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Account Status:</span>
                <button type="button" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  className={`flex items-center gap-1.5 text-sm px-3 py-1 rounded-full ${form.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {form.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  {form.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2">
              {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
              {isEdit ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Stats Panel ────────────────────────────────────────────────────────────
function StatsPanel({ users, stats, loadingStats }) {
  const activeUsers   = users.filter(u => u.is_active).length;
  const adminCount    = users.filter(u => u.user_type === 'admin').length;
  const digitizerCount = users.filter(u => u.user_type === 'digitizer').length;
  const recentLogins  = users.filter(u => u.last_login && new Date(u.last_login) > new Date(Date.now() - 7 * 86400000)).length;

  const cards = [
    { label: 'Total Users',    value: users.length,   icon: Users,        color: 'bg-blue-50 text-blue-600',   border: 'border-blue-100' },
    { label: 'Active Users',   value: activeUsers,    icon: UserCheck,    color: 'bg-green-50 text-green-600', border: 'border-green-100' },
    { label: 'Admins',         value: adminCount,     icon: Shield,       color: 'bg-purple-50 text-purple-600', border: 'border-purple-100' },
    { label: 'Logged in (7d)', value: recentLogins,   icon: Activity,     color: 'bg-amber-50 text-amber-600', border: 'border-amber-100' },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ label, value, icon: Icon, color, border }) => (
          <div key={label} className={`bg-white rounded-xl border ${border} p-5 flex items-center gap-4`}>
            <div className={`w-11 h-11 rounded-xl ${color} bg-opacity-80 flex items-center justify-center shrink-0`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">{value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Role Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-gray-500" /> Role Breakdown
        </h3>
        <div className="space-y-3">
          {USER_TYPES.map(type => {
            const count = users.filter(u => u.user_type === type).length;
            const pct = users.length ? Math.round((count / users.length) * 100) : 0;
            const colors = USER_TYPE_COLORS[type] || { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
            return (
              <div key={type} className="flex items-center gap-3">
                <div className="w-20 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${colors.badge}`}>{type}</span>
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-2 rounded-full ${colors.dot}`} style={{ width: `${pct}%`, transition: 'width 0.5s ease' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700 w-6 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Office Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Database className="w-4 h-4 text-gray-500" /> Office Breakdown
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['NEB Head Office', 'NEB Province Office', 'NEB Exam Center'].map(office => {
            const count = users.filter(u => u.office_type === office).length;
            return (
              <div key={office} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-100">
                <div className="text-2xl font-bold text-gray-800">{count}</div>
                <div className="text-xs text-gray-500 mt-1">{office}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* DB Stats */}
      {stats && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-500" /> Database Activity
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">{stats.total ?? '—'}</div>
              <div className="text-xs text-blue-500 mt-1">Total Records</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">{stats.today ?? '—'}</div>
              <div className="text-xs text-green-500 mt-1">Added Today</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg col-span-2 sm:col-span-1">
              <div className="text-sm font-bold text-purple-700 truncate">{stats.topSubjects?.[0]?.subject || 'N/A'}</div>
              <div className="text-xs text-purple-500 mt-1">Top Subject</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Users Panel ─────────────────────────────────────────────────────────────
function UsersPanel({ users, loading, currentUser, onAdd, onEdit, onDelete, onRefresh }) {
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const filtered = users.filter(u => {
    const matchSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.phone || '').includes(search) ||
      u.office_type.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.user_type === filterRole;
    return matchSearch && matchRole;
  });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search users…"
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
            />
          </div>
          {/* Role filter */}
          <select
            value={filterRole} onChange={e => setFilterRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            {USER_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <button onClick={onRefresh} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shrink-0"
        >
          <UserPlus className="w-4 h-4" /> Add User
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No users found</p>
            <p className="text-sm mt-1">Try adjusting your search or filter</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Office</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Last Login</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(u => {
                  const roleColor = USER_TYPE_COLORS[u.user_type] || { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
                  return (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm shadow-sm">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800 flex items-center gap-1.5 leading-tight">
                              {u.name}
                              {u.id === currentUser?.id && (
                                <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">You</span>
                              )}
                            </div>
                            <div className="text-gray-400 text-xs mt-0.5">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-gray-600 hidden sm:table-cell">{u.phone || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-md">{u.office_type}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${roleColor.badge}`}>
                          {u.user_type}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        {u.last_login ? (
                          <div>
                            <div className="text-xs text-gray-600">{new Date(u.last_login).toLocaleDateString()}</div>
                            <div className="text-xs text-gray-400">{new Date(u.last_login).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => onEdit(u)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          {u.id !== currentUser?.id && (
                            <button onClick={() => onDelete(u)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
              Showing {filtered.length} of {users.length} users
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CSV helpers ──────────────────────────────────────────────────────
const CSV_COLUMNS = ['name', 'class', 'reg_number', 'symbol_number', 'date_of_birth', 'school'];
const CSV_HEADERS = ['Name *', 'Class', 'Reg Number', 'Symbol Number *', 'Date of Birth * (YYYY-MM-DD)', 'School'];

function downloadTemplate() {
  const rows = [
    CSV_HEADERS.join(','),
    'Ram Bahadur Shrestha,11,R-2024-001,12345,2006-05-15,Everest Secondary School',
    'Sita Kumari Thapa,11,R-2024-002,12346,2006-08-20,Himalaya Higher Secondary',
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = 'students_template.csv'; a.click();
}

function mapHeader(h) {
  const s = h.replace(/[*()]/g, '').trim().toLowerCase().replace(/\s+/g, '_');
  if (/name/.test(s) && !/school/.test(s)) return 'name';
  if (/class/.test(s)) return 'class';
  if (/reg/.test(s)) return 'reg_number';
  if (/symbol/.test(s)) return 'symbol_number';
  if (/birth|dob/.test(s)) return 'date_of_birth';
  if (/school/.test(s)) return 'school';
  return s;
}

function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  const colMap = lines[0].split(',').map(mapHeader);
  return lines.slice(1).map(line => {
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    colMap.forEach((col, i) => { row[col] = vals[i] || ''; });
    return row;
  }).filter(r => r.name || r.symbol_number);
}

function parseXLSX(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
  return raw.map(r => {
    const mapped = {};
    Object.entries(r).forEach(([k, v]) => {
      const col = mapHeader(String(k));
      // Format dates from cellDates:true — they arrive as JS Date objects
      if (v instanceof Date) {
        mapped[col] = v.toISOString().slice(0, 10);
      } else {
        mapped[col] = String(v).trim();
      }
    });
    return mapped;
  }).filter(r => r.name || r.symbol_number);
}

function BulkImportModal({ onClose, onDone, authFetch }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | preview | result
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('skip'); // skip | update
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [parseError, setParseError] = useState('');

  const handleFile = (file) => {
    if (!file) return;
    setParseError('');
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    if (ext === 'csv') {
      reader.onload = (e) => {
        const parsed = parseCSV(e.target.result);
        if (parsed.length === 0) { setParseError('No valid rows found. Check the file format.'); return; }
        setRows(parsed);
        setStep('preview');
      };
      reader.readAsText(file);
    } else if (ext === 'xlsx' || ext === 'xls') {
      reader.onload = (e) => {
        try {
          const parsed = parseXLSX(e.target.result);
          if (parsed.length === 0) { setParseError('No valid rows found. Check the first sheet and column headers.'); return; }
          setRows(parsed);
          setStep('preview');
        } catch (err) { setParseError('Failed to read Excel file: ' + err.message); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setParseError('Unsupported file type. Please upload a .csv, .xlsx or .xls file.');
    }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const res = await authFetch('/api/students/bulk', {
        method: 'POST',
        body: JSON.stringify({ students: rows, mode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResult(data);
      setStep('result');
    } catch (e) { setParseError(e.message); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Bulk Import Students</h3>
              <p className="text-xs text-gray-400">
                {step === 'upload' && 'Upload a CSV file to import multiple students at once'}
                {step === 'preview' && `${rows.length} students ready to import`}
                {step === 'result' && 'Import complete'}
              </p>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {parseError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{parseError}</span>
            </div>
          )}

          {/* STEP 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              >
                <FileText className="w-12 h-12 text-gray-300" />
                <p className="text-sm font-medium text-gray-600">Drop file here, or click to browse</p>
                <div className="flex gap-2">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">.csv</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">.xlsx</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">.xls</span>
                </div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" className="hidden"
                  onChange={e => handleFile(e.target.files?.[0])} />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-600">Required CSV columns (order matters):</p>
                <div className="grid grid-cols-2 gap-1">
                  {CSV_HEADERS.map((h, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${h.includes('*') ? 'bg-red-400' : 'bg-gray-300'}`} />
                      {h}
                    </div>
                  ))}
                </div>
                <button onClick={downloadTemplate}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium pt-1">
                  <Download className="w-3.5 h-3.5" /> Download template CSV
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Duplicate mode toggle */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-amber-800 mb-2">When symbol number already exists:</p>
                  <div className="flex gap-2">
                    <button onClick={() => setMode('skip')}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${mode === 'skip' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                      Skip duplicate
                    </button>
                    <button onClick={() => setMode('update')}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${mode === 'update' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                      Update existing
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200 flex justify-between">
                  <span>Preview ({rows.length} rows)</span>
                  <button onClick={() => setStep('upload')} className="text-blue-600 hover:text-blue-700">← Change file</button>
                </div>
                <div className="overflow-x-auto max-h-72">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white border-b border-gray-100">
                      <tr className="text-gray-500">
                        <th className="text-left px-3 py-2">#</th>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Symbol No.</th>
                        <th className="text-left px-3 py-2">Reg No.</th>
                        <th className="text-left px-3 py-2">Class</th>
                        <th className="text-left px-3 py-2">DOB</th>
                        <th className="text-left px-3 py-2">School</th>
                        <th className="px-3 py-2 text-center">OK?</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {rows.map((r, i) => {
                        const ok = r.name && r.symbol_number && r.date_of_birth;
                        return (
                          <tr key={i} className={ok ? '' : 'bg-red-50'}>
                            <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                            <td className="px-3 py-1.5 font-medium text-gray-800">{r.name || <span className="text-red-500">missing</span>}</td>
                            <td className="px-3 py-1.5 font-mono text-blue-700">{r.symbol_number || <span className="text-red-500">missing</span>}</td>
                            <td className="px-3 py-1.5 text-gray-500">{r.reg_number || '—'}</td>
                            <td className="px-3 py-1.5">{r.class || '—'}</td>
                            <td className="px-3 py-1.5 text-gray-500">{r.date_of_birth || <span className="text-red-500">missing</span>}</td>
                            <td className="px-3 py-1.5 text-gray-500">{r.school || '—'}</td>
                            <td className="px-3 py-1.5 text-center">{ok ? <Check className="w-3.5 h-3.5 text-green-500 mx-auto" /> : <X className="w-3.5 h-3.5 text-red-500 mx-auto" />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Result */}
          {step === 'result' && result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{result.inserted}</p>
                  <p className="text-xs text-green-600 mt-1">Inserted</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{result.updated ?? 0}</p>
                  <p className="text-xs text-blue-600 mt-1">Updated</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-500">{result.skipped}</p>
                  <p className="text-xs text-gray-400 mt-1">Skipped / Errors</p>
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="border border-red-200 rounded-xl overflow-hidden">
                  <p className="text-xs font-semibold text-red-700 bg-red-50 px-3 py-2 border-b border-red-200">
                    {result.errors.length} row(s) had errors:
                  </p>
                  <div className="max-h-40 overflow-y-auto divide-y divide-red-50">
                    {result.errors.map((e, i) => (
                      <div key={i} className="px-3 py-2 text-xs text-red-600 flex gap-2">
                        <span className="font-mono font-medium">{e.symbol_number}</span>
                        <span className="text-red-400">—</span>
                        <span>{e.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex gap-3 justify-end">
          {step === 'upload' && (
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          )}
          {step === 'preview' && (
            <>
              <button onClick={() => setStep('upload')} className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Back</button>
              <button onClick={doImport} disabled={importing}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
                {importing ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Upload className="w-4 h-4" />}
                Import {rows.length} Students
              </button>
            </>
          )}
          {step === 'result' && (
            <button onClick={() => { onDone(); onClose(); }}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Students Panel ───────────────────────────────────────────────────
const EMPTY_STUDENT = { name: '', class: '', reg_number: '', symbol_number: '', date_of_birth: '', school: '' };

function StudentsPanel() {
  const { authFetch } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null); // null = closed, {} = add/edit
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);

  const fetchStudents = async (q = '') => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/students${q ? `?search=${encodeURIComponent(q)}` : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStudents(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStudents(); }, []);

  const handleSearch = (v) => { setSearch(v); fetchStudents(v); };

  const saveStudent = async () => {
    if (!form.name || !form.symbol_number || !form.date_of_birth) {
      setError('Name, Symbol Number and Date of Birth are required.'); return;
    }
    setSaving(true);
    try {
      const isEdit = !!form.id;
      const res = await authFetch(isEdit ? `/api/students/${form.id}` : '/api/students', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(form)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setForm(null);
      fetchStudents(search);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const doDelete = async (id) => {
    try {
      const res = await authFetch(`/api/students/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setDeleteConfirm(null);
      fetchStudents(search);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Search name, symbol, school…"
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{students.length} students</span>
          <button onClick={() => fetchStudents(search)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowBulkImport(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium">
            <Upload className="w-4 h-4" /> Import Student
          </button>
          <button onClick={() => setForm({ ...EMPTY_STUDENT })}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> Add Student
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No students found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Symbol No.</th>
                  <th className="text-left px-3 py-2.5 font-semibold hidden sm:table-cell">Reg. No.</th>
                  <th className="text-left px-3 py-2.5 font-semibold hidden sm:table-cell">Class</th>
                  <th className="text-left px-3 py-2.5 font-semibold hidden md:table-cell">D.O.B</th>
                  <th className="text-left px-3 py-2.5 font-semibold hidden lg:table-cell">School</th>
                  <th className="px-3 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {students.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{s.name}</td>
                    <td className="px-3 py-2 font-mono text-blue-700">{s.symbol_number}</td>
                    <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">{s.reg_number || '—'}</td>
                    <td className="px-3 py-2 hidden sm:table-cell">{s.class || '—'}</td>
                    <td className="px-3 py-2 text-gray-500 hidden md:table-cell">
                      {s.date_of_birth ? new Date(s.date_of_birth).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500 hidden lg:table-cell">{s.school || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => setForm({ ...s, date_of_birth: s.date_of_birth?.slice(0, 10) })}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteConfirm(s)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">{form.id ? 'Edit Student' : 'Add Student'}</h3>
              <button onClick={() => setForm(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {[['name','Name *','text'],['class','Class','text'],['reg_number','Reg. Number','text'],
              ['symbol_number','Symbol Number *','text'],['date_of_birth','Date of Birth *','date'],
              ['school','School','text']].map(([field, label, type]) => (
              <div key={field}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                <input type={type} value={form[field] || ''}
                  onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setForm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveStudent} disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
                {saving ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                {form.id ? 'Save Changes' : 'Add Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkImport && (
        <BulkImportModal
          authFetch={authFetch}
          onClose={() => setShowBulkImport(false)}
          onDone={() => fetchStudents(search)}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="font-semibold text-gray-800 mb-2">Delete Student?</h3>
            <p className="text-sm text-gray-500 mb-5">Remove <strong>{deleteConfirm.name}</strong> (Symbol: {deleteConfirm.symbol_number}) from the system? This does not delete their marks.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={() => doDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Publish Result Panel ─────────────────────────────────────────────────────
const EXAM_TYPES = ['SEE', 'NEB Grade 12', 'Other'];

function PublishResultPanel() {
  const { authFetch } = useAuth();
  const [publications, setPublications] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ exam_type: 'NEB Grade 12', year: '', notes: '' });
  const [publishing, setPublishing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pubRes, yearRes] = await Promise.all([
        authFetch('/api/publications'),
        authFetch('/api/publications/available-years'),
      ]);
      setPublications(await pubRes.json());
      setAvailableYears(await yearRes.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePublish = async () => {
    if (!form.year) { setError('Please select a year.'); return; }
    setPublishing(true); setError(''); setSuccess('');
    try {
      const res = await authFetch('/api/publications', {
        method: 'POST', body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(`Result published: ${form.exam_type} ${form.year}`);
      setConfirm(false);
      fetchData();
    } catch (e) { setError(e.message); }
    finally { setPublishing(false); }
  };

  const handleUnpublish = async (pub) => {
    try {
      const res = await authFetch(`/api/publications/${pub.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setSuccess(`Unpublished: ${pub.exam_type} ${pub.year}`);
      fetchData();
    } catch (e) { setError(e.message); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
          <CheckCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Publish Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Publish Result</h3>
            <p className="text-xs text-gray-400">Select exam type and year, then confirm to make results visible to students.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Exam Type</label>
            <select value={form.exam_type} onChange={e => setForm(f => ({ ...f, exam_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
              {EXAM_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Year</label>
            <select value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
              <option value="">— Select Year —</option>
              {availableYears
                .filter(y => y.exam_type === form.exam_type)
                .map(y => <option key={y.year} value={y.year}>{y.year}</option>)}
            </select>
            {availableYears.filter(y => y.exam_type === form.exam_type).length === 0 && (
              <p className="text-xs text-amber-500 mt-1">No digitized records found for this exam type yet.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. First batch"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>

        <div className="mt-4">
          <button onClick={() => { setError(''); setConfirm(true); }} disabled={!form.year}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl text-sm font-semibold transition-colors">
            <Globe className="w-4 h-4" /> Publish Result
          </button>
        </div>
      </div>

      {/* Published list */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 text-sm">Publication History</h3>
          <button onClick={fetchData} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <span className="w-6 h-6 border-2 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : publications.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Globe className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No results published yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs uppercase text-gray-500 tracking-wide border-b border-gray-100">
                <th className="text-left px-5 py-2.5 font-semibold">Exam Type</th>
                <th className="text-left px-5 py-2.5 font-semibold">Year</th>
                <th className="text-left px-5 py-2.5 font-semibold hidden sm:table-cell">Published By</th>
                <th className="text-left px-5 py-2.5 font-semibold hidden md:table-cell">Published At</th>
                <th className="text-center px-5 py-2.5 font-semibold">Status</th>
                <th className="px-5 py-2.5 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {publications.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-800">{p.exam_type}</td>
                  <td className="px-5 py-3 font-mono text-blue-700">{p.year}</td>
                  <td className="px-5 py-3 text-gray-500 hidden sm:table-cell">{p.published_by_name || '—'}</td>
                  <td className="px-5 py-3 text-gray-400 text-xs hidden md:table-cell">{fmtDate(p.published_at)}</td>
                  <td className="px-5 py-3 text-center">
                    {p.is_published ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-medium">
                        <Globe className="w-3 h-3" /> Live
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 border border-gray-200 rounded-full text-xs font-medium">
                        <EyeOff className="w-3 h-3" /> Hidden
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {p.is_published ? (
                      <button onClick={() => handleUnpublish(p)}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-lg">
                        <EyeOff className="w-3 h-3" /> Unpublish
                      </button>
                    ) : (
                      <button onClick={() => { setForm({ exam_type: p.exam_type, year: p.year, notes: p.notes || '' }); setConfirm(true); }}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 border border-emerald-200 rounded-lg">
                        <Globe className="w-3 h-3" /> Re-publish
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm Dialog */}
      {confirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-center mb-1">Confirm Publication</h3>
            <p className="text-sm text-gray-500 text-center mb-1">You are about to publish:</p>
            <p className="text-center font-bold text-lg text-emerald-700 mb-1">{form.exam_type}</p>
            <p className="text-center font-bold text-2xl text-gray-900 mb-4">{form.year}</p>
            <p className="text-xs text-gray-400 text-center mb-6">
              Once published, all students with matching symbol numbers will be able to view their results online.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handlePublish} disabled={publishing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                {publishing
                  ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <Globe className="w-4 h-4" />}
                Publish Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DB Records Panel ───────────────────────────────────────────────────────────
function DBRecordsPanel() {
  const { authFetch } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editRow, setEditRow] = useState(null); // {id, year, subject, centre, roll_number, marks_in_figure, marks_in_words}
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [resetConfirm, setResetConfirm] = useState(false);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/records');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecords(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const resetAllRecords = async () => {
    try {
      const res = await authFetch('/api/records', { method: 'DELETE' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setResetConfirm(false);
      fetchRecords();
    } catch (e) { setError(e.message); }
  };

  useEffect(() => { fetchRecords(); }, []);

  const filtered = records.filter(r =>
    !search ||
    String(r.roll_number).includes(search) ||
    (r.subject || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.centre || '').toLowerCase().includes(search.toLowerCase()) ||
    String(r.year || '').includes(search)
  );

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/records/${editRow.id}`, {
        method: 'PUT',
        body: JSON.stringify(editRow)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setEditRow(null);
      fetchRecords();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const doDelete = async (id) => {
    try {
      const res = await authFetch(`/api/records/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setDeleteConfirm(null);
      fetchRecords();
    } catch (e) { setError(e.message); }
  };

  const EditInput = ({ field, type = 'text' }) => (
    <input
      type={type}
      value={editRow[field] ?? ''}
      onChange={e => setEditRow(r => ({ ...r, [field]: e.target.value }))}
      className="w-full px-2 py-1 border border-blue-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search roll, subject, centre, year…"
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{filtered.length} of {records.length} records</span>
          <button onClick={fetchRecords} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setResetConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-medium">
            <Trash2 className="w-3.5 h-3.5" /> Reset All Records
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Database className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-3 py-2.5 font-semibold">ID</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Year</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Subject</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Centre</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Roll No.</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Marks (Fig)</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Marks (Words)</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Saved At</th>
                  <th className="px-3 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(r => {
                  const isEditing = editRow?.id === r.id;
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50 ${isEditing ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-2 text-gray-400">{r.id}</td>
                      <td className="px-3 py-2">{isEditing ? <EditInput field="year" /> : r.year}</td>
                      <td className="px-3 py-2">{isEditing ? <EditInput field="subject" /> : r.subject}</td>
                      <td className="px-3 py-2">{isEditing ? <EditInput field="centre" /> : r.centre}</td>
                      <td className="px-3 py-2 font-medium">{isEditing ? <EditInput field="roll_number" /> : r.roll_number}</td>
                      <td className="px-3 py-2">{isEditing ? <EditInput field="marks_in_figure" /> : r.marks_in_figure}</td>
                      <td className="px-3 py-2">{isEditing ? <EditInput field="marks_in_words" /> : r.marks_in_words}</td>
                      <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <div className="flex gap-1">
                            <button onClick={saveEdit} disabled={saving}
                              className="p-1 text-green-600 hover:bg-green-50 rounded">
                              {saving ? <span className="w-3 h-3 border border-green-600/30 border-t-green-600 rounded-full animate-spin block" /> : <Check className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => setEditRow(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <button onClick={() => setEditRow({ ...r })} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setDeleteConfirm(r)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800 text-center mb-1">Delete Record</h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              Delete record for roll <strong>{deleteConfirm.roll_number}</strong> ({deleteConfirm.subject} {deleteConfirm.year})?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={() => doDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}

      {resetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800 text-center mb-1">Reset All DB Records?</h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              This will permanently delete <strong>all {records.length} records</strong> from the database. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setResetConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={resetAllRecords}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">Delete All</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Action type config ───────────────────────────────────────────────────────
const ACTION_CONFIG = {
  login:          { color: 'bg-green-100 text-green-700',  label: 'Login' },
  logout:         { color: 'bg-gray-100 text-gray-600',    label: 'Logout' },
  extract:        { color: 'bg-blue-100 text-blue-700',    label: 'Extract' },
  save:           { color: 'bg-indigo-100 text-indigo-700',label: 'Save' },
  edit:           { color: 'bg-amber-100 text-amber-700',  label: 'Edit' },
  delete:         { color: 'bg-red-100 text-red-700',      label: 'Delete' },
  approve:        { color: 'bg-teal-100 text-teal-700',    label: 'Approve' },
  reject:         { color: 'bg-orange-100 text-orange-700',label: 'Reject' },
  settings_change:{ color: 'bg-purple-100 text-purple-700',label: 'Settings' },
};

// ─── Activity Panel ───────────────────────────────────────────────────────────
function ActivityPanel({ users }) {
  const { authFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const [resetConfirm, setResetConfirm] = useState(false);

  const resetActivityLog = async () => {
    try {
      const res = await authFetch('/api/activity', { method: 'DELETE' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setResetConfirm(false);
      fetchActivity(0);
    } catch (err) { console.error(err); }
  };

  const fetchActivity = async (newOffset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: newOffset });
      if (filterUser) params.set('user_id', filterUser);
      if (filterAction) params.set('action_type', filterAction);
      const res = await authFetch(`/api/activity?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRows(data.rows);
      setTotal(data.total);
      setOffset(newOffset);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchActivity(0); }, [filterUser, filterAction]);

  const fmt = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Users</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Actions</option>
            {Object.entries(ACTION_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button onClick={() => fetchActivity(0)}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setResetConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-medium">
            <Trash2 className="w-3.5 h-3.5" /> Clear Log
          </button>
        </div>
        <span className="text-xs text-gray-400">{total} total events</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No activity found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="text-left px-4 py-3">Time</th>
                    <th className="text-left px-4 py-3">User</th>
                    <th className="text-left px-4 py-3">Action</th>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(row => {
                    const cfg = ACTION_CONFIG[row.action_type] || { color: 'bg-gray-100 text-gray-600', label: row.action_type };
                    const details = row.new_values ? (() => { try { return JSON.parse(row.new_values); } catch { return null; } })() : null;
                    return (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(row.performed_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {row.user_name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="font-medium text-gray-800 text-xs leading-tight">{row.user_name || 'Unknown'}</div>
                              <div className="text-gray-400 text-xs">{row.user_email || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs max-w-xs truncate">{row.description || '—'}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-400">
                          {details ? (
                            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs truncate max-w-[200px] block">
                              {Object.entries(details).map(([k,v]) => `${k}: ${v}`).join(' · ')}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {resetConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-center mb-1">Clear Activity Log?</h3>
                  <p className="text-gray-500 text-sm text-center mb-6">This will permanently delete all {total} activity log entries. This cannot be undone.</p>
                  <div className="flex gap-3">
                    <button onClick={() => setResetConfirm(false)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
                    <button onClick={resetActivityLog} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">Clear All</button>
                  </div>
                </div>
              </div>
            )}
            {total > LIMIT && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button disabled={offset === 0} onClick={() => fetchActivity(offset - LIMIT)}
                    className="px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-100">
                    Previous
                  </button>
                  <button disabled={offset + LIMIT >= total} onClick={() => fetchActivity(offset + LIMIT)}
                    className="px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-100">
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Grace Marks Panel ────────────────────────────────────────────────────────
function GraceMarksPanel() {
  const { authFetch } = useAuth();
  const [settings, setSettings] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [applying, setApplying] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [filterExam, setFilterExam] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [gracedStudents, setGracedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudents, setShowStudents] = useState(false);

  const fetchAll = async () => {
    setLoadingSettings(true);
    setError('');
    try {
      const sRes = await authFetch('/api/grace-settings');
      const sData = await sRes.json();
      if (sRes.ok) setSettings(Array.isArray(sData) ? sData : []);
      else setError(sData.error || 'Failed to load grace settings');
    } catch (e) { setError(e.message); }
    try {
      const mRes = await authFetch('/api/grace-marks/summary');
      const mData = await mRes.json();
      if (mRes.ok) setSummary(Array.isArray(mData) ? mData : []);
    } catch (_) { /* summary is non-critical */ }
    setLoadingSettings(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchGracedStudents = async () => {
    setLoadingStudents(true);
    try {
      const params = new URLSearchParams();
      if (filterExam) params.set('exam_type', filterExam);
      if (filterYear) params.set('year', filterYear);
      if (filterSubject) params.set('subject', filterSubject);
      if (studentSearch) params.set('search', studentSearch);
      const res = await authFetch(`/api/grace-marks/students?${params}`);
      const data = await res.json();
      if (res.ok) { setGracedStudents(Array.isArray(data) ? data : []); setShowStudents(true); }
      else setError(data.error || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoadingStudents(false); }
  };

  const exportCSV = () => {
    if (!gracedStudents.length) return;
    const header = ['Roll No', 'Exam Type', 'Year', 'Subject', 'Centre', 'Original Score', 'Pass Marks', 'Grace Marks', 'Effective Total', 'Grace Applied At'];
    const rows = gracedStudents.map(r => [
      r.roll_number, r.exam_type, r.year, r.subject, r.centre,
      r.marks_in_figure, r.pass_marks, r.grace_marks,
      Number(r.effective_total ?? 0).toFixed(2),
      r.grace_applied_at ? new Date(r.grace_applied_at).toLocaleString() : ''
    ]);
    const csv = [header, ...rows].map(row => row.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `graced-students-${filterExam || 'all'}-${filterYear || 'all'}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const saveSettings = async (row) => {
    try {
      const res = await authFetch(`/api/grace-settings/${encodeURIComponent(row.exam_type)}`, {
        method: 'PUT',
        body: JSON.stringify({
          max_grace: Number(row.max_grace),
          threshold_pct: Number(row.threshold_pct),
          apply_theory: row.apply_theory ? 1 : 0,
          apply_practical: row.apply_practical ? 1 : 0,
          notes: row.notes || ''
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setEditingRow(null);
      setSuccess('Settings saved');
      fetchAll();
    } catch (e) { setError(e.message); }
  };

  const runDryRun = async () => {
    if (!filterExam) { setError('Select an exam type first'); return; }
    setApplying(true); setDryRunResult(null); setApplyResult(null); setError('');
    try {
      const res = await authFetch('/api/grace-marks/apply', {
        method: 'POST',
        body: JSON.stringify({ exam_type: filterExam, year: filterYear || undefined, subject: filterSubject || undefined, dry_run: true })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setDryRunResult(d);
    } catch (e) { setError(e.message); }
    finally { setApplying(false); }
  };

  const applyGrace = async () => {
    if (!filterExam) { setError('Select an exam type first'); return; }
    setApplying(true); setApplyResult(null); setError('');
    try {
      const res = await authFetch('/api/grace-marks/apply', {
        method: 'POST',
        body: JSON.stringify({ exam_type: filterExam, year: filterYear || undefined, subject: filterSubject || undefined, dry_run: false })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setApplyResult(d);
      setDryRunResult(null);
      setSuccess(`Grace marks applied to ${d.applied} record(s)`);
      fetchAll();
      if (d.applied > 0) { setShowStudents(true); fetchGracedStudents(); }
    } catch (e) { setError(e.message); }
    finally { setApplying(false); }
  };

  const resetGrace = async () => {
    setResetting(true); setError('');
    try {
      const res = await authFetch('/api/grace-marks/reset', {
        method: 'POST',
        body: JSON.stringify({ exam_type: filterExam || undefined, year: filterYear || undefined, subject: filterSubject || undefined })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setApplyResult(null); setDryRunResult(null);
      setSuccess(d.message);
      fetchAll();
      fetchGracedStudents();
    } catch (e) { setError(e.message); }
    finally {
      setResetting(false);
      setResetConfirm(false);
    }
  };

  const EXAM_TYPES = ['NEB Grade 12', 'SEE', 'Other'];

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /><span className="flex-1">{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
          <CheckCircle2 className="w-4 h-4 shrink-0" /><span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* How it works */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
        <div className="text-sm text-indigo-700 space-y-1">
          <p className="font-semibold">How grace marks work</p>
          <p>A student is eligible if their score is below pass marks but within the <strong>threshold %</strong> of the pass mark. Grace awarded = <span className="font-mono bg-indigo-100 px-1 rounded text-xs">min(gap, max_grace)</span>, bringing them to exactly the pass mark or the max grace limit — whichever is smaller. Original score is preserved; <strong>grace_marks</strong> is stored separately. Effective total = original + grace.</p>
        </div>
      </div>

      {/* Settings table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-500" />Grace Marks Settings</h3>
          <button onClick={fetchAll} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {loadingSettings ? (
          <div className="flex items-center justify-center h-24"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                <th className="text-left px-4 py-3">Exam Type</th>
                <th className="text-left px-4 py-3">Max Grace Marks</th>
                <th className="text-left px-4 py-3">Threshold (% of pass)</th>
                <th className="text-left px-4 py-3">Theory</th>
                <th className="text-left px-4 py-3">Practical</th>
                <th className="text-left px-4 py-3">Notes</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {settings.map(s => {
                const isEdit = editingRow?.exam_type === s.exam_type;
                const row = isEdit ? editingRow : s;
                return (
                  <tr key={s.exam_type} className={isEdit ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-gray-800">{s.exam_type}</td>
                    <td className="px-4 py-3">
                      {isEdit ? <input type="number" min="0" max="10" step="0.5" value={row.max_grace}
                        onChange={e => setEditingRow(r => ({ ...r, max_grace: e.target.value }))}
                        className="w-20 px-2 py-1 border border-blue-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        : <span className="font-semibold text-indigo-700">{s.max_grace}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {isEdit ? <input type="number" min="1" max="50" step="1" value={row.threshold_pct}
                        onChange={e => setEditingRow(r => ({ ...r, threshold_pct: e.target.value }))}
                        className="w-20 px-2 py-1 border border-blue-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        : <span>{s.threshold_pct}%</span>}
                    </td>
                    <td className="px-4 py-3">
                      {isEdit ? <input type="checkbox" checked={!!row.apply_theory}
                        onChange={e => setEditingRow(r => ({ ...r, apply_theory: e.target.checked }))}
                        className="w-4 h-4 accent-blue-600" />
                        : (s.apply_theory ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-gray-300" />)}
                    </td>
                    <td className="px-4 py-3">
                      {isEdit ? <input type="checkbox" checked={!!row.apply_practical}
                        onChange={e => setEditingRow(r => ({ ...r, apply_practical: e.target.checked }))}
                        className="w-4 h-4 accent-blue-600" />
                        : (s.apply_practical ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-gray-300" />)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px]">
                      {isEdit ? <input value={row.notes || ''} onChange={e => setEditingRow(r => ({ ...r, notes: e.target.value }))}
                        className="w-full px-2 py-1 border border-blue-300 rounded text-xs focus:outline-none" />
                        : s.notes}
                    </td>
                    <td className="px-4 py-3">
                      {isEdit ? (
                        <div className="flex gap-1">
                          <button onClick={() => saveSettings(editingRow)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditingRow(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <button onClick={() => setEditingRow({ ...s })} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-3.5 h-3.5" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Apply / Reset controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Apply Grace Marks</h3>
        <div className="flex flex-wrap gap-3">
          <select value={filterExam} onChange={e => { setFilterExam(e.target.value); setDryRunResult(null); setApplyResult(null); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">— Select Exam Type —</option>
            {EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input value={filterYear} onChange={e => setFilterYear(e.target.value)} placeholder="Year (optional)"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40" />
          <input value={filterSubject} onChange={e => setFilterSubject(e.target.value)} placeholder="Subject (optional)"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={runDryRun} disabled={applying || !filterExam}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl text-sm font-medium disabled:opacity-40">
            {applying ? <span className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-500 rounded-full animate-spin" /> : <Info className="w-4 h-4" />}
            Preview (Dry Run)
          </button>
          <button onClick={applyGrace} disabled={applying || !filterExam}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium disabled:opacity-40">
            {applying ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Award Grace Marks
          </button>
          <button onClick={() => setResetConfirm(true)} disabled={resetting}
            className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-xl text-sm font-medium disabled:opacity-40">
            <RotateCcw className="w-4 h-4" /> Reset Grace Marks
          </button>
          <button onClick={fetchGracedStudents} disabled={loadingStudents}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-xl text-sm font-medium disabled:opacity-40">
            {loadingStudents ? <span className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-500 rounded-full animate-spin" /> : <Users className="w-4 h-4" />}
            View Graced Students
          </button>
        </div>

        {/* Dry run preview */}
        {dryRunResult && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-semibold text-amber-800">
              Dry Run — {dryRunResult.applied} eligible student(s) would receive grace marks · {dryRunResult.skipped} skipped
            </p>
            {dryRunResult.details.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-amber-700 border-b border-amber-200">
                    <th className="text-left py-1 pr-4">Roll</th>
                    <th className="text-left py-1 pr-4">Subject</th>
                    <th className="text-left py-1 pr-4">Score</th>
                    <th className="text-left py-1 pr-4">Pass</th>
                    <th className="text-left py-1 pr-4">Gap</th>
                    <th className="text-left py-1">Grace</th>
                  </tr></thead>
                  <tbody className="divide-y divide-amber-100">
                    {dryRunResult.details.slice(0, 20).map(d => (
                      <tr key={d.id} className="text-amber-900">
                        <td className="py-1 pr-4 font-medium">{d.roll}</td>
                        <td className="py-1 pr-4 max-w-[120px] truncate">{d.subject}</td>
                        <td className="py-1 pr-4">{d.fig}</td>
                        <td className="py-1 pr-4">{d.pass}</td>
                        <td className="py-1 pr-4 text-red-600">{d.gap}</td>
                        <td className="py-1 font-semibold text-green-700">+{d.grace}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {dryRunResult.details.length > 20 && <p className="text-xs text-amber-600 mt-2">…and {dryRunResult.details.length - 20} more</p>}
              </div>
            )}
            <button onClick={applyGrace} disabled={applying}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">
              <Sparkles className="w-4 h-4" /> Confirm & Apply {dryRunResult.applied} Grace Marks
            </button>
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-green-800">
              ✓ Grace marks applied to <strong>{applyResult.applied}</strong> student(s). {applyResult.skipped} were ineligible or already graced.
            </p>
            <p className="text-xs text-green-600 mt-1">Settings used: max {applyResult.settings?.max_grace} marks · within {applyResult.settings?.threshold_pct}% of pass mark</p>
          </div>
        )}
      </div>

      {/* Summary table */}
      {summary.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800 text-sm">Grace Marks Summary by Exam & Year</h3>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
              <th className="text-left px-4 py-3">Exam Type</th>
              <th className="text-left px-4 py-3">Year</th>
              <th className="text-left px-4 py-3">Total Records</th>
              <th className="text-left px-4 py-3">Graced Students</th>
              <th className="text-left px-4 py-3">Total Grace Awarded</th>
              <th className="text-left px-4 py-3">Max Given</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {summary.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{r.exam_type || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{r.year || '—'}</td>
                  <td className="px-4 py-3">{r.total_records ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold ${(r.graced_count ?? 0) > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>{r.graced_count ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-green-700 font-medium">{Number(r.total_grace_awarded ?? 0).toFixed(1)}</td>
                  <td className="px-4 py-3">{Number(r.max_grace_given ?? 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Graced Students List */}
      {showStudents && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3 justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" />
              Graced Students
              {gracedStudents.length > 0 && (
                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{gracedStudents.length}</span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchGracedStudents()}
                  placeholder="Roll no / centre…"
                  className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                />
              </div>
              <button onClick={fetchGracedStudents} disabled={loadingStudents}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button onClick={exportCSV} disabled={!gracedStudents.length}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg text-xs font-medium disabled:opacity-40">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
              <button onClick={() => setShowStudents(false)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {loadingStudents ? (
            <div className="flex items-center justify-center h-32">
              <span className="w-6 h-6 border-2 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : gracedStudents.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No graced students found for the selected filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase tracking-wide">
                    <th className="text-left px-4 py-2.5">Roll No</th>
                    <th className="text-left px-4 py-2.5">Exam Type</th>
                    <th className="text-left px-4 py-2.5">Year</th>
                    <th className="text-left px-4 py-2.5">Subject</th>
                    <th className="text-left px-4 py-2.5">Centre</th>
                    <th className="text-right px-4 py-2.5">Original</th>
                    <th className="text-right px-4 py-2.5">Pass</th>
                    <th className="text-right px-4 py-2.5 text-indigo-600">Grace</th>
                    <th className="text-right px-4 py-2.5 text-green-700">Total</th>
                    <th className="text-left px-4 py-2.5">Applied At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {gracedStudents.map(r => (
                    <tr key={r.id} className="hover:bg-indigo-50/30">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.roll_number}</td>
                      <td className="px-4 py-2.5 text-gray-500">{r.exam_type}</td>
                      <td className="px-4 py-2.5 text-gray-500">{r.year}</td>
                      <td className="px-4 py-2.5 max-w-[120px] truncate">{r.subject}</td>
                      <td className="px-4 py-2.5 text-gray-500 max-w-[100px] truncate">{r.centre}</td>
                      <td className="px-4 py-2.5 text-right">{r.marks_in_figure}</td>
                      <td className="px-4 py-2.5 text-right text-gray-400">{r.pass_marks}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="text-indigo-700 font-semibold">+{Number(r.grace_marks).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-semibold">
                          {Number(r.effective_total ?? 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">
                        {r.grace_applied_at ? new Date(r.grace_applied_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reset confirm dialog */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800 text-center mb-1">Reset Grace Marks?</h3>
            <p className="text-gray-500 text-sm text-center mb-2">
              This will remove grace marks from{' '}
              <strong>{filterExam || 'all exam types'}</strong>
              {filterYear ? `, year ${filterYear}` : ''}
              {filterSubject ? `, subject ${filterSubject}` : ''}.
            </p>
            <p className="text-xs text-gray-400 text-center mb-6">Original scores are untouched. You can re-apply grace marks anytime.</p>
            <div className="flex gap-3">
              <button onClick={() => setResetConfirm(false)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={resetGrace} disabled={resetting} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">
                {resetting ? 'Resetting…' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Admin Dashboard ─────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { authFetch, user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await authFetch('/api/users');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load users');
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await authFetch('/api/stats');
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`/api/users/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const tabs = [
    { id: 'overview',  label: 'Overview',               icon: BarChart3 },
    { id: 'users',     label: `Users (${users.length})`, icon: Users },
    { id: 'students',  label: 'Students',               icon: GraduationCap },
    { id: 'publish',   label: 'Publish Results',        icon: Megaphone },
    { id: 'records',   label: 'DB Records',             icon: Database },
    { id: 'grace',     label: 'Grace Marks',            icon: Sparkles },
    { id: 'activity',  label: 'Activity Log',           icon: Activity },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            Admin Dashboard
          </h2>
          <p className="text-gray-500 text-sm mt-1">Manage users and view system overview</p>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <StatsPanel users={users} stats={stats} />
      )}
      {activeTab === 'users' && (
        <UsersPanel
          users={users}
          loading={loading}
          currentUser={currentUser}
          onAdd={() => setModal({ mode: 'add', user: null })}
          onEdit={u => setModal({ mode: 'edit', user: u })}
          onDelete={u => setDeleteConfirm(u)}
          onRefresh={fetchUsers}
        />
      )}
      {activeTab === 'students' && <StudentsPanel />}
      {activeTab === 'publish'  && <PublishResultPanel />}
      {activeTab === 'records' && <DBRecordsPanel />}
      {activeTab === 'grace'   && <GraceMarksPanel />}
      {activeTab === 'activity' && (
        <ActivityPanel users={users} />
      )}

      {/* User Modal */}
      {modal && (
        <UserModal
          user={modal.user}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); fetchUsers(); }}
        />
      )}

      {/* Delete Confirm Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800 text-center mb-1">Delete User</h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              Are you sure you want to delete <strong className="text-gray-800">{deleteConfirm.name}</strong>?<br />
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
